/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    useEffect, useMemo, useRef, useState,
} from 'react';
import {
    arrayOf,
    bool,
    func,
    node,
    number,
    oneOf,
    shape,
    string,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { getBrand } from '../../../pages/Account/state/ducks/Common/Common-Selectors';
import ProductFacetGroup from './ProductFacetGroup';
import SelectedProductFacets from './SelectedProductFacets';
import brandColors from './brandColors';

const useStyles = makeStyles(() => ({
    paper: {
        margin: 0,
        maxHeight: '100%',
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        borderRadius: 0,
    },
    root: {
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        position: 'relative',
        padding: '16px',
        borderBottom: '1px solid #E5E5E5',
        textAlign: 'center',
        flexShrink: 0,
        backgroundColor: '#ffffff',
    },
    backButton: {
        position: 'absolute',
        left: '8px',
        top: '8px',
    },
    clearAll: {
        position: 'absolute',
        right: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        border: 'none',
        background: 'transparent',
        textDecoration: 'underline',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        color: '#000000',
    },
    title: {
        fontSize: '18px',
        fontWeight: 600,
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        minHeight: 0,
        padding: '0px 24px',
        WebkitOverflowScrolling: 'touch',
    },
    footer: {
        borderTop: '1px solid #E5E5E5',
        padding: '12px',
        backgroundColor: '#ffffff',
        flexShrink: 0,
    },
    fullButton: {
        width: '100%',
        textTransform: 'none',
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: '26px',
        letterSpacing: '0.72px',
        borderRadius: '8px',
        padding: '15px 28px',
        backgroundColor: ({ primaryButtonColor }) => primaryButtonColor,
        color: '#ffffff',
        '&:hover': {
            backgroundColor: ({ primaryButtonColor }) => primaryButtonColor,
        },
    },
    trigger: {
        border: 'none',
        padding: 0,
        margin: 0,
        background: 'transparent',
        textAlign: 'inherit',
        cursor: 'pointer',
    },
    selectedWrapper: {
        borderBottom: '1px solid #E5E5E5',
        padding: '16px 0px',
        flexShrink: 0,
        backgroundColor: '#ffffff',
    },
}));

const ProductFacetMobile = ({
    title,
    productFacets,
    selectedProductFacets,
    onApply,
    children,
    sortByOptions,
    selectedSortValue,
    onSortChange,
    sortByTitle,
    resultsCount,
    itemType,
    stateless,
    loading,
    trackEvent,
}) => {
    const brand = useSelector(getBrand);
    const classes = useStyles({
        primaryButtonColor: brandColors?.[brand?.domain]?.primary || '#2F2F2F',
    });
    const [open, setOpen] = useState(false);
    const [localSelectedFacets, setLocalSelectedFacets] = useState(selectedProductFacets);
    const [localSortValue, setLocalSortValue] = useState(selectedSortValue || '');
    const prevOpenRef = useRef(false);

    useEffect(() => {
        if (!stateless && open && !prevOpenRef.current) {
            setLocalSelectedFacets(selectedProductFacets);
            setLocalSortValue(selectedSortValue || '');
        }
        prevOpenRef.current = open;
    }, [open, selectedProductFacets, selectedSortValue, stateless]);

    const selectedFacetsValues = stateless ? selectedProductFacets : localSelectedFacets;
    const selectedSortFacetValue = stateless ? (selectedSortValue || '') : localSortValue;

    const hasSortBy = sortByOptions.length > 0;

    const handleFacetChange = (facetName, value, checked, entry) => {
        if (stateless) {
            const nextSelectedFacets = checked
                ? [...selectedProductFacets, value]
                : selectedProductFacets.filter((v) => v !== value);
            if (checked) {
                trackEvent('Click', { name: entry.name, value, count: entry.count });
            }
            onApply(nextSelectedFacets);
            return;
        }
        setLocalSelectedFacets((prev) => {
            if (checked) return [...prev, value];
            return prev.filter((v) => v !== value);
        });
    };

    const handleApply = () => {
        if (stateless) {
            setOpen(false);
            return;
        }
        onApply(localSelectedFacets);
        if (hasSortBy) onSortChange(localSortValue);
        setOpen(false);
    };

    const handleRemove = (value) => {
        if (stateless) {
            onApply(selectedProductFacets.filter((v) => v !== value));
            return;
        }
        setLocalSelectedFacets((prev) => prev.filter((v) => v !== value));
    };

    const handleRemoveSort = () => {
        if (stateless) {
            onSortChange('');
            return;
        }
        setLocalSortValue('');
    };

    const selectedFacets = useMemo(() => (
        selectedFacetsValues
            .map((value) => {
                for (let i = 0; i < productFacets.length; i += 1) {
                    const facet = productFacets[i];
                    const entry = facet.entries.find((e) => e.value === value);
                    if (entry) {
                        return { label: entry.name, value: entry.value };
                    }
                }
                return null;
            })
            .filter((item) => item !== null)
    ), [selectedFacetsValues, productFacets]);

    const hasSelections = selectedFacets.length > 0 || !!selectedSortFacetValue;

    const selectedSortFacet = useMemo(() => {
        if (!selectedSortFacetValue) return null;
        const selectedSort = sortByOptions.find((option) => option.value === selectedSortFacetValue);
        return {
            label: selectedSort?.label || selectedSortFacetValue,
            value: selectedSortFacetValue,
        };
    }, [selectedSortFacetValue, sortByOptions]);

    const showResultsText = resultsCount !== null && resultsCount !== undefined
        ? `Show results (${resultsCount} ${resultsCount === 1 ? 'item' : 'items'})`
        : 'Show results';

    const handleOnClick = () => {
        setOpen(true);
        trackEvent();
    };

    return (
        <>
            <button type="button" className={classes.trigger} onClick={handleOnClick}>
                {children}
            </button>
            <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: classes.paper }}>
                <div className={classes.root}>
                    <div className={classes.header}>
                        <IconButton className={classes.backButton} onClick={() => setOpen(false)}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography className={classes.title}>{title}</Typography>
                        {hasSelections && (
                            <button
                                type="button"
                                className={classes.clearAll}
                                onClick={() => {
                                    if (stateless) {
                                        onApply([]);
                                        if (hasSortBy) onSortChange('');
                                    } else {
                                        setLocalSelectedFacets([]);
                                        setLocalSortValue('');
                                    }
                                }}
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                    {hasSelections && (
                        <div className={classes.selectedWrapper}>
                            <SelectedProductFacets
                                selectedFacets={selectedFacets}
                                selectedSortFacet={selectedSortFacet}
                                onRemove={handleRemove}
                                onRemoveSort={handleRemoveSort}
                                hideClearAll
                                inlineMargin
                                loading={loading}
                            />
                        </div>
                    )}
                    <div className={classes.body}>
                        {hasSortBy && (
                            <ProductFacetGroup
                                facet={{
                                    name: sortByTitle,
                                    displayOpen: true,
                                    entries: sortByOptions.map((option) => ({
                                        name: option.label,
                                        value: option.value,
                                        count: 0,
                                        from: null,
                                        to: null,
                                        tooltip: null,
                                    })),
                                }}
                                selectedValues={selectedSortFacetValue ? [selectedSortFacetValue] : []}
                                onChange={(_facetName, value, checked) => {
                                    if (checked) {
                                        trackEvent('Click', null, value);
                                    }
                                    if (stateless) {
                                        onSortChange(checked ? value : '');
                                        return;
                                    }
                                    setLocalSortValue(checked ? value : '');
                                }}
                                itemType="radio"
                                loading={loading}
                            />
                        )}
                        {productFacets.map((facet) => (
                            <ProductFacetGroup
                                key={facet.name}
                                facet={facet}
                                selectedValues={selectedFacetsValues}
                                onChange={handleFacetChange}
                                itemType={itemType}
                                loading={loading}
                            />
                        ))}
                    </div>
                    <div className={classes.footer}>
                        <Button className={classes.fullButton} color="primary" variant="contained" onClick={handleApply}>
                            {showResultsText}
                        </Button>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

ProductFacetMobile.propTypes = {
    title: string,
    productFacets: arrayOf(shape({
        name: string.isRequired,
        displayOpen: bool,
        entries: arrayOf(shape({
            name: string.isRequired,
            value: string.isRequired,
            count: number,
            from: number,
            to: number,
            tooltip: string,
        })),
    })).isRequired,
    selectedProductFacets: arrayOf(string),
    onApply: func,
    children: node.isRequired,
    sortByOptions: arrayOf(shape({
        label: string.isRequired,
        value: string.isRequired,
    })),
    selectedSortValue: string,
    onSortChange: func,
    sortByTitle: string,
    resultsCount: number,
    itemType: oneOf(['checkbox', 'radio']),
    stateless: bool,
    loading: bool,
    trackEvent: func,
};

ProductFacetMobile.defaultProps = {
    title: 'Filters',
    selectedProductFacets: [],
    onApply: () => {},
    sortByOptions: [],
    selectedSortValue: '',
    onSortChange: () => {},
    sortByTitle: 'Sort by',
    resultsCount: null,
    itemType: 'checkbox',
    stateless: false,
    loading: false,
    trackEvent: () => {},
};

export default ProductFacetMobile;
